import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import cartProductEditor from 'domains/cart_product_editor';

import actions from './wall_preview_rooms.actions';
import wallPreviewsAdapter from './wall_preview_rooms.adapter';
import utils from './wall_preview_rooms.utils';

import type { LoadingStatus } from 'types/common_types';

import type {
  EditUserRoomDraft,
  RawWallPreviewSampleRoom,
  WallPreviewActiveRoom,
  WallPreviewRoom,
  WallPreviewSampleRoom,
  WallPreviewUserRoom,
} from './wall_preview_rooms.types';

type WallPreviewsInitialState = {
  whichWallPreview: WallPreviewActiveRoom | null;
  editRoomDrafts: Record<string, Partial<EditUserRoomDraft>>;
  status: LoadingStatus;
};

const initialState: WallPreviewsInitialState = {
  whichWallPreview: null,
  editRoomDrafts: {},
  status: 'idle',
};

const loadedState = wallPreviewsAdapter.addMany(
  wallPreviewsAdapter.getInitialState(initialState),
  utils.sampleRooms.map(
    (r: RawWallPreviewSampleRoom) => utils.normalizeRoomData(r) as WallPreviewSampleRoom
  )
);

const wallPreview = createSlice({
  name: 'wallPreview',
  initialState: loadedState,
  reducers: {
    setWhichWallPreview(state, { payload }: PayloadAction<WallPreviewRoom>) {
      return {
        ...state,
        whichWallPreview: payload,
      };
    },
    unsetWhichWallPreview(state) {
      return {
        ...state,
        whichWallPreview: null,
      };
    },
    createRoomEditDraft(state, { payload }: PayloadAction<number>) {
      return {
        ...state,
        editRoomDrafts: {
          [payload]: {},
        },
      };
    },
    updateRoomEditDraft(
      state,
      { payload }: PayloadAction<{ id: number; update: EditUserRoomDraft }>
    ) {
      return {
        ...state,
        editRoomDrafts: {
          ...state.editRoomDrafts,
          [payload.id]: payload.update,
        },
      };
    },
    resetEditDrafts(state) {
      return {
        ...state,
        editRoomDrafts: {},
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.wallPreviewImageLoad.fulfilled, (state, { payload }) => {
        if (!state.whichWallPreview) return state;

        const { blobSrc } = payload;
        return {
          ...state,
          whichWallPreview: { ...state.whichWallPreview, blobSrc },
        };
      })
      .addCase(actions.fetchUserRooms.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.fetchUserRooms.fulfilled, (state, { payload }) => {
        const normalized = payload.map(utils.normalizeRoomData) as WallPreviewUserRoom[];
        const update = wallPreviewsAdapter.addMany(state, normalized);
        update.status = 'idle';
        return update;
      })
      .addCase(actions.fetchUserRooms.rejected, (state) => {
        // Handle an error, send more details to sentry, etc
        return { ...state, status: 'error' };
      })
      .addCase(actions.createUserRooms.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.createUserRooms.fulfilled, (state, { payload }) => {
        const normalized = payload.map(utils.normalizeRoomData) as WallPreviewUserRoom[];
        const update = wallPreviewsAdapter.addMany(state, normalized);
        const drafts = normalized.reduce(
          (acc: Record<string, Partial<EditUserRoomDraft>>, room) => {
            acc[room.id] = {};
            return acc;
          },
          {}
        );

        update.editRoomDrafts = drafts;
        update.status = 'idle';
        return update;
      })
      .addCase(actions.createUserRooms.rejected, (state) => {
        // Handle an error, send more details to sentry, etc
        return { ...state, status: 'error' };
      })
      .addCase(actions.saveUserRooms.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.saveUserRooms.fulfilled, (state, { payload }) => {
        const normalized = payload.map(utils.normalizeRoomData) as WallPreviewUserRoom[];
        const updates = normalized.map((room) => ({ id: room.id, changes: room }));
        const update = wallPreviewsAdapter.updateMany(state, updates);

        update.editRoomDrafts = {};
        update.status = 'idle';
        return update;
      })
      .addCase(actions.saveUserRooms.rejected, (state) => {
        // Handle an error, send more details to sentry, etc
        return { ...state, status: 'error' };
      })
      .addCase(actions.deleteUserRoom.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.deleteUserRoom.fulfilled, (state, { payload }) => {
        return wallPreviewsAdapter.removeOne(state, payload);
      })
      .addCase(actions.deleteUserRoom.rejected, (state) => {
        // Handle an error, send more details to sentry, etc
        return { ...state, status: 'error' };
      })
      .addCase(cartProductEditor.actions.pending, (state) => {
        return { ...state, whichWallPreview: null };
      });
  },
});

export default wallPreview;
